import { useState, useRef } from 'react'
import { Upload, X, CheckCircle, Loader } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function MuxUpload({ onComplete, setStatus }) {
  const { api } = useAuth()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [phase, setPhase] = useState('') // 'uploading' | 'processing' | 'ready'
  const [dragOver, setDragOver] = useState(false)
  const fileRef = useRef(null)
  const xhrRef = useRef(null)
  const pollRef = useRef(null)

  function handleFiles(fileList) {
    const file = fileList[0]
    if (!file) return

    if (!file.name.match(/\.(mp4|mov|mkv|avi|webm|m4v|ts|mts)$/i)) {
      setStatus('Only video files are accepted')
      return
    }

    startUpload(file)
  }

  async function startUpload(file) {
    setUploading(true)
    setProgress(0)
    setPhase('uploading')

    const title = file.name.replace(/\.[^.]+$/, '')

    try {
      // Step 1: Get signed upload URL from Mux via our worker
      setStatus(`Requesting upload URL for "${title}"...`)
      const { uploadUrl, uploadId } = await api('/mux/upload-url', {
        method: 'POST',
        body: JSON.stringify({ title })
      })

      if (!uploadUrl) throw new Error('No upload URL returned from Mux')

      // Step 2: Upload file directly to Mux via PUT
      setStatus(`Uploading "${title}" to Mux...`)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhrRef.current = xhr

        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100))
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve()
          } else {
            reject(new Error(`Mux upload failed: ${xhr.status}`))
          }
        })

        xhr.addEventListener('error', () => reject(new Error('Upload failed')))
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')))

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4')
        xhr.send(file)
      })

      // Step 3: Poll for asset creation
      setPhase('processing')
      setProgress(100)
      setStatus(`"${title}" uploaded — Mux is processing...`)

      const assetId = await pollUploadStatus(uploadId)

      setPhase('ready')
      setStatus(`✅ "${title}" ready in Mux (${assetId})`)
      onComplete(assetId)
    } catch (e) {
      if (e.message !== 'Upload cancelled') {
        setStatus(`Error: ${e.message}`)
      }
    } finally {
      cleanup()
    }
  }

  async function pollUploadStatus(uploadId) {
    const maxAttempts = 120 // 6 minutes at 3s intervals
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => { pollRef.current = setTimeout(r, 3000) })

      try {
        const data = await api(`/mux/uploads/${uploadId}`)
        if (data.status === 'asset_created' && data.assetId) {
          return data.assetId
        }
        if (data.status === 'errored') {
          throw new Error('Mux processing failed')
        }
      } catch (e) {
        if (e.message === 'Mux processing failed') throw e
      }
    }
    throw new Error('Timed out waiting for Mux to process the upload')
  }

  function handleCancel() {
    if (xhrRef.current) xhrRef.current.abort()
    cleanup()
    setStatus('Upload cancelled')
  }

  function cleanup() {
    setUploading(false)
    setProgress(0)
    setPhase('')
    xhrRef.current = null
    if (pollRef.current) clearTimeout(pollRef.current)
    pollRef.current = null
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleDrop(e) {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <div className="mux-upload-section">
      <h4>
        <Upload size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
        Upload to Mux
      </h4>

      {uploading ? (
        <div className="mux-upload-progress">
          <div className="mux-upload-progress-bar-track">
            <div
              className={`mux-upload-progress-bar-fill ${phase === 'processing' ? 'processing' : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mux-upload-progress-info">
            {phase === 'uploading' && <span>{progress}% uploading</span>}
            {phase === 'processing' && (
              <span className="mux-upload-processing">
                <Loader size={12} className="spinning" /> Mux processing...
              </span>
            )}
            {phase === 'ready' && (
              <span className="mux-upload-ready">
                <CheckCircle size={12} /> Ready
              </span>
            )}
            {phase !== 'ready' && (
              <button onClick={handleCancel} className="mux-upload-cancel-btn">
                <X size={14} /> Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`mux-upload-drop-zone ${dragOver ? 'drag-over' : ''}`}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
        >
          <Upload size={24} style={{ opacity: 0.4, marginBottom: 8 }} />
          <p>Drop a video file here or click to browse</p>
          <span style={{ fontSize: 11, opacity: 0.5 }}>Uploads directly to Mux — MP4, MOV, MKV, WebM</span>
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            onChange={e => handleFiles(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  )
}
